import { ReportMessageButton } from "@/components/ReportMessageButton";
import { useState, useEffect, useRef } from "react";
import { useChatWidget } from "@/hooks/useChatWidget";
import { useDirectMessages } from "@/hooks/useDirectMessages";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ArrowLeft, Check, Minus, Pencil, Pin, Send, Trash2, X } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { format, isToday, isYesterday } from "date-fns";
import { useNavigate } from "react-router-dom";
import { useIsMobile } from "@/hooks/use-mobile";

export const FloatingChatWidget = () => {
  const { conversationId, isOpen, closeChat, prefilledText, consumePrefilledText } = useChatWidget();
  const { user } = useAuth();
  const isMobile = useIsMobile();
  const navigate = useNavigate();

  // Don't render on mobile or when not open
  if (isMobile || !isOpen || !conversationId || !user) return null;

  return (
    <ChatPanel
      conversationId={conversationId}
      userId={user.id}
      onClose={closeChat}
      prefilledText={prefilledText}
      onPrefilledTextConsumed={consumePrefilledText}
    />
  );
};

interface ChatPanelProps {
  conversationId: string;
  userId: string;
  onClose: () => void;
  prefilledText?: string | null;
  onPrefilledTextConsumed?: () => void;
}

const ChatPanel = ({ conversationId, userId, onClose, prefilledText, onPrefilledTextConsumed }: ChatPanelProps) => {
  const { messages, loading, sendMessage, editMessage, deleteForEveryone, deleteForMe, markRead } = useDirectMessages(conversationId);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Grow the composer with its text (up to a cap), then shrink back after sending
  const resizeTextarea = () => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 128)}px`;
  };

  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setText(e.target.value);
    resizeTextarea();
  };
  const [otherUser, setOtherUser] = useState<{
    id: string;
    first_name: string | null;
    avatar_url: string | null;
  } | null>(null);
  const [pinnedListing, setPinnedListing] = useState<{ id: string; title: string } | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchOther = async () => {
      const { data: participants } = await supabase
        .from("conversation_participants")
        .select("user_id")
        .eq("conversation_id", conversationId)
        .neq("user_id", userId);

      if (participants && participants.length > 0) {
        const { data: profile } = await supabase
          .from("public_profiles")
          .select("id, first_name, avatar_url")
          .eq("id", participants[0].user_id)
          .single();

        if (profile) {
          setOtherUser({
            id: profile.id!,
            first_name: profile.first_name,
            avatar_url: profile.avatar_url,
          });
        }
      }
    };
    fetchOther();

    (async () => {
      const { data: conv } = await supabase
        .from("conversations")
        .select("noticeboard_post_id")
        .eq("id", conversationId)
        .maybeSingle();
      const postId = (conv as any)?.noticeboard_post_id;
      if (!postId) { setPinnedListing(null); return; }
      const { data: post } = await supabase
        .from("noticeboard_posts_public" as any)
        .select("id, title")
        .eq("id", postId)
        .maybeSingle();
      if (post) setPinnedListing({ id: (post as any).id, title: (post as any).title });
    })();
  }, [conversationId, userId]);

  useEffect(() => {
    markRead();
  }, [messages.length, markRead]);

  useEffect(() => {
    if (scrollRef.current && !minimized) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, minimized]);

  // Pre-fill the composer when openChat is called with a prefilled message
  useEffect(() => {
    if (prefilledText) {
      setText(prefilledText);
      setMinimized(false);
      onPrefilledTextConsumed?.();
      // Wait for the textarea to render with the new text before measuring it
      requestAnimationFrame(resizeTextarea);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefilledText, onPrefilledTextConsumed]);

  const clearComposer = () => {
    setText("");
    setEditingId(null);
    if (textareaRef.current) textareaRef.current.style.height = "auto";
  };

  const handleSubmit = async () => {
    if (!text.trim() || sending) return;
    setSending(true);
    try {
      if (editingId) await editMessage(editingId, text);
      else await sendMessage(text);
      clearComposer();
    } catch {
      // error logged in hook
    } finally {
      setSending(false);
    }
  };

  // WhatsApp-style: the message being edited loads into the typing field
  const startEdit = (id: string, current: string) => {
    setEditingId(id);
    setText(current);
    document.getElementById(`msg-${id}`)?.scrollIntoView({ block: "center", behavior: "smooth" });
    requestAnimationFrame(() => {
      const el = textareaRef.current;
      if (!el) return;
      el.focus();
      el.setSelectionRange(el.value.length, el.value.length);
      resizeTextarea();
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setText("");
    if (textareaRef.current) textareaRef.current.style.height = "auto";
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
    if (e.key === "Escape" && editingId) cancelEdit();
  };

  const formatMessageTime = (dateStr: string) => {
    const d = new Date(dateStr);
    if (isToday(d)) return format(d, "HH:mm");
    if (isYesterday(d)) return "Yesterday " + format(d, "HH:mm");
    return format(d, "dd MMM HH:mm");
  };

  return (
    <div className="fixed bottom-6 right-6 z-50 hidden md:flex flex-col w-[360px] shadow-xl rounded-2xl border border-border bg-card overflow-hidden">
      {/* Header */}
      <div className="flex items-center gap-2 px-4 py-3 border-b border-border/50 bg-primary/5">
        {otherUser && (
          <div
            className="flex items-center gap-2 flex-1 cursor-pointer min-w-0"
            onClick={() => navigate(`/user/${otherUser.id}`)}
          >
            <Avatar className="h-7 w-7 flex-shrink-0">
              <AvatarImage src={otherUser.avatar_url || undefined} />
              <AvatarFallback className="bg-primary text-primary-foreground text-xs">
                {otherUser.first_name?.charAt(0)?.toUpperCase() || "?"}
              </AvatarFallback>
            </Avatar>
            <span className="text-sm font-semibold text-foreground truncate">
              {otherUser.first_name}
            </span>
          </div>
        )}
        <div className="flex items-center gap-0.5">
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 text-muted-foreground hover:text-foreground"
            onClick={() => setMinimized(!minimized)}
          >
            <Minus className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 text-muted-foreground hover:text-foreground"
            onClick={onClose}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Pinned noticeboard listing */}
      {pinnedListing && !minimized && (
        <div className="bg-background px-3 pt-2 pb-2 border-b border-border">
          <button
            type="button"
            onClick={() => navigate(`/noticeboard/${pinnedListing.id}`)}
            className="w-full flex items-center gap-2 rounded-lg border border-primary/20 bg-primary/10 px-3 py-2 text-left text-sm no-underline hover:bg-primary/15 transition-colors"
          >
            <Pin className="h-3.5 w-3.5 text-primary flex-shrink-0" />
            <span className="text-[10px] uppercase tracking-wide text-muted-foreground">Listing</span>
            <span className="font-medium text-foreground truncate text-[13px]">{pinnedListing.title}</span>
          </button>
        </div>
      )}


      {/* Body - collapsible */}
      {!minimized && (
        <>
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 py-3 space-y-2 max-h-[400px] min-h-[200px]">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary" />
              </div>
            ) : messages.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground text-xs">
                Start a conversation 💬
              </div>
            ) : (
              messages.map((msg) => {
                const isMe = msg.sender_id === userId;
                const isEditing = editingId === msg.id;
                const isDeleted = !!msg.deleted_at;
                return (
                  <div key={msg.id} id={`msg-${msg.id}`} className={`group flex ${isMe ? "justify-end" : "justify-start"}`}>
                    <div className="min-w-0 max-w-[80%] pl-8">
                      <div className="relative">
                        <div
                          className={`px-3 py-1.5 rounded-2xl text-[13px] whitespace-pre-wrap break-words ${
                            isMe
                              ? "bg-primary text-primary-foreground rounded-br-md"
                              : "bg-muted text-foreground rounded-bl-md"
                          }${isEditing ? " ring-2 ring-primary/50" : ""}${isDeleted ? " italic opacity-70" : ""}`}
                        >
                          {isDeleted ? "This message was deleted" : msg.message}
                        </div>
                        <div className="absolute bottom-0 right-full mr-1 flex items-end gap-1">
                          {isMe && !isEditing && !isDeleted && (
                            <Button
                              variant="ghost"
                              size="icon"
                              aria-label="Edit message"
                              className="h-7 w-7 opacity-0 group-hover:opacity-100 focus-visible:opacity-100 transition-opacity flex-shrink-0"
                              onClick={() => startEdit(msg.id, msg.message)}
                            >
                              <Pencil className="h-3 w-3 text-muted-foreground" />
                            </Button>
                          )}
                          {!isDeleted && (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  aria-label="Delete message"
                                  className="h-7 w-7 opacity-0 group-hover:opacity-100 focus-visible:opacity-100 transition-opacity flex-shrink-0"
                                >
                                  <Trash2 className="h-3 w-3 text-muted-foreground" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align={isMe ? "end" : "start"}>
                                {isMe && (
                                  <DropdownMenuItem onClick={() => deleteForEveryone(msg.id)}>
                                    Delete for everyone
                                  </DropdownMenuItem>
                                )}
                                <DropdownMenuItem onClick={() => deleteForMe(msg.id)}>
                                  Delete for me
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          )}
                        </div>
                      </div>
                      <p className={`text-[9px] text-muted-foreground mt-0.5 ${isMe ? "text-right" : "text-left"}`}>
                        {formatMessageTime(msg.created_at)}
                        {msg.edited_at && !isDeleted ? " · edited" : ""}
                      </p>
                      {!isMe && !isDeleted && (
                        <ReportMessageButton
                          messageId={msg.id}
                          messageText={msg.message}
                          senderId={msg.sender_id}
                          senderName={otherUser?.first_name || "this member"}
                          context="dm"
                          contextId={conversationId}
                          className="-ml-1.5"
                        />
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Input */}
          <div className="border-t border-border/50 px-3 py-2">
            {editingId && (
              <div className="mb-1.5 flex items-center gap-2 rounded-lg bg-muted px-3 py-1.5">
                <Pencil className="h-3 w-3 text-primary flex-shrink-0" />
                <span className="text-[11px] font-medium text-muted-foreground">Editing message</span>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label="Cancel edit"
                  className="ml-auto h-5 w-5 text-muted-foreground"
                  onClick={cancelEdit}
                  disabled={sending}
                >
                  <X className="h-3 w-3" />
                </Button>
              </div>
            )}
            <div className="flex items-start gap-2">
              <Textarea
                ref={textareaRef}
                value={text}
                onChange={handleTextChange}
                onKeyDown={handleKeyDown}
                placeholder={editingId ? "Edit your message..." : "Type a message..."}
                className="flex-1 min-h-9 max-h-32 resize-none overflow-y-auto text-sm py-2 leading-snug border-0 focus-visible:ring-0 focus-visible:ring-offset-0 px-0"
                disabled={sending}
                rows={1}
              />
              <Button
                size="icon"
                onClick={handleSubmit}
                aria-label={editingId ? "Save changes" : "Send"}
                disabled={!text.trim() || sending}
                className="h-9 w-9 rounded-full flex-shrink-0"
              >
                {editingId ? <Check className="h-3.5 w-3.5" /> : <Send className="h-3.5 w-3.5" />}
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
